import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { useModelService } from 'services/model/model.service';
import { useIntl } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useEntriesDemandReportData } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { useContext, useEffect, useState } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import {
  buildStandardColumns,
  createCellExportOptions,
  DemandTableProps,
  DemandTableRow,
  digestData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';
import { ProductDetailsTableRow } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductDetailsTableRow';
import { DEMAND_MONITORING_ROOT } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandHome';
import { useNavigate } from 'react-router-dom';

interface DemandBrandTableRow extends DemandTableRow {
  prdBrand: string;
}

export function BrandTable({ merchantData, filters }: DemandTableProps) {
  const intl = useIntl();
  const navigate = useNavigate();

  const { getMeasureByMetric, getAttributes } = useModelService();
  const [prdBrand] = getAttributes(['prdBrand']);
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');
  const ordQty = getMeasureByMetric('ordQty', 'OD');

  const columnHelper = createColumnHelper<DemandBrandTableRow>();

  const defaultColumns = buildStandardColumns(ordQty, intl);

  const buildColumns = () => [
    columnHelper.accessor('prdBrand', {
      id: 'prdBrand',
      header: () => prdBrand.name,
      cell: info => info.getValue(),
    }),
  ];

  const cols: ColumnDef<DemandBrandTableRow>[] = (buildColumns() as ColumnDef<ProductDetailsTableRow>[]).concat(
    defaultColumns as ColumnDef<ProductDetailsTableRow>[]
  ) as ColumnDef<DemandBrandTableRow>[];

  const { data } = useEntriesDemandReportData(prdBrand.code, filters);

  const { currentDataVersion } = useContext(TenantConfigContext);

  const [tableRows, setTableRows] = useState<DemandBrandTableRow[]>([]);

  useEffect(() => {
    if (currentDataVersion) {
      digestData(
        merchantData,
        data,
        prdBrand,
        ordGmvVal,
        ordQty,
        entry => ({
          prdBrand: prdBrand.getValue(entry.attributes),
        }),
        setTableRows
      );
    }
  }, [data, merchantData, currentDataVersion]);

  const showDetails = (row: Row<DemandBrandTableRow>) => {
    navigate(`${DEMAND_MONITORING_ROOT}/detail/${prdBrand.code}/${row.original.prdBrand}${location.search}`);
  };

  return (
    <>
      <DataTableContextProvider>
        <DataTable
          rows={tableRows}
          onRowClick={showDetails}
          columns={cols}
          titleId="insights.brand"
          defaultSortingState={[{ id: 'ordGmvVal', desc: true }]}
          exportConfigOptions={createCellExportOptions('Brands')}
        />
      </DataTableContextProvider>
    </>
  );
}
